import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';

const PAGE_SIZE = 50;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const currency = (value) => Number(value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const displayDate = (value) => {
  if (!value) return 'N/A';
  const parsed = parseISO(String(value).slice(0, 10));
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MM/dd/yyyy');
};
const badgeClass = {
  'Status Correct': 'bg-green-100 text-green-800',
  'Should Likely Be Closed': 'bg-blue-100 text-blue-800',
  'Should Likely Be Open': 'bg-amber-100 text-amber-800',
  'Needs Human Review': 'bg-red-100 text-red-800',
};
const views = [
  ['all', 'All', 'Every PO'],
  ['closed-unreconciled', 'Closed unreconciled', 'Closed but money doesn\'t tie out'],
  ['open-eligible', 'Open eligible', 'Open POs that look ready to close'],
  ['closed-should-open', 'Closed should open', 'Closed POs still owed goods'],
  ['needs-review', 'Needs review', 'Needs a human decision'],
  ['duplicate-po', 'Duplicate PO', 'Same PO number seen twice'],
  ['receiving-flag', 'Receiving flag', 'Over/under received'],
];
const statuses = [
  ['all', 'All'],
  ['OPEN', 'Open'],
  ['CLOSED', 'Closed'],
  ['CANCELLED', 'Cancelled'],
];
const closedViews = new Set(['all', 'closed-unreconciled', 'needs-review', 'duplicate-po']);

const PoStatusAuditReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const lockedClosed = location.pathname === ROUTES.REPORTS_CLOSED_PO_RECONCILIATION;
  const reportId = lockedClosed ? 'closed-po-reconciliation' : 'po-status-audit';
  const reportPath = lockedClosed
    ? '/api/v1/reports/closed-po-reconciliation'
    : '/api/v1/reports/po-status-audit';
  const [view, setView] = useState(lockedClosed ? 'closed-unreconciled' : 'all');
  const [status, setStatus] = useState(lockedClosed ? 'CLOSED' : 'all');
  const [vendor, setVendor] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ view, status });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (vendor) query.set('vendor', vendor);
    if (search) query.set('search', search);
    return query;
  }, [page, search, status, vendor, view]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${reportPath}?${params()}`, { headers: authHeaders() });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load PO status audit');
      setData(json.data || []);
      setColumns(json.columns || []);
      setSummary(json.summary || {});
      setTotalPages(json.totalPages || 1);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [params, reportPath]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, vendor, view]);

  const exportData = async () => {
    const response = await fetch(`${reportPath}/export?${params(false)}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export PO status audit');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const renderCell = (row, column) => {
    if (column.key === 'po_number') {
      return <Link className="text-blue-700 hover:underline" to={`${ROUTES.PURCHASE_ORDERS}/${row.po_id}`}>{row.po_number || 'N/A'}</Link>;
    }
    if (column.key === 'classification') {
      return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${badgeClass[row[column.key]] || 'bg-gray-100 text-gray-700'}`}>{row[column.key]}</span>;
    }
    if (column.key === 'receiving_flag') {
      return <span className="font-medium text-amber-700">{row[column.key] || 'N/A'}</span>;
    }
    if (column.key === 'closed_verdict' && lockedClosed) {
      const clean = row[column.key] === 'CLEAN';
      return <span className={clean ? 'font-semibold text-green-700' : 'font-semibold text-red-700'}>{row[column.key] || 'N/A'}</span>;
    }
    if (column.key === 'bill_agreement') {
      return row[column.key] === 'vendor settled' ? 'Vendor settled' : row[column.key];
    }
    if (column.type === 'currency') return currency(row[column.key]);
    if (column.type === 'date') return displayDate(row[column.key]);
    return row[column.key] === null || row[column.key] === undefined || row[column.key] === '' ? 'N/A' : row[column.key];
  };

  const cards = useMemo(() => Object.entries(summary.classification_by_status || {}).map(([key, count]) => ({ key, count })), [summary]);
  const materiality = Object.entries(summary.materiality || {});
  const closedVerdicts = Object.entries(summary.closed_verdict || {});
  const visibleColumns = columns.filter((column) => lockedClosed || column.key !== 'closed_verdict');
  const defaultView = lockedClosed ? 'closed-unreconciled' : 'all';
  const defaultStatus = lockedClosed ? 'CLOSED' : 'all';
  const selectedView = views.find(([key]) => key === view)?.[1] || 'All';
  const selectedStatus = statuses.find(([key]) => key === status)?.[1] || 'All';
  const hasFilters = view !== defaultView || status !== defaultStatus || vendor !== '' || search !== '';
  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <Menu as="div" className="relative">
        <MenuButton type="button" className={`inline-flex h-[38px] items-center gap-2 whitespace-nowrap rounded-md border bg-white px-3 text-sm ${view !== defaultView ? 'border-gray-900' : 'border-gray-300'}`}>
          <span className="text-gray-500">View:</span>
          <span className="font-semibold">{selectedView}</span>
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </MenuButton>
        <MenuItems anchor="bottom end" className="z-30 mt-2 min-w-[15rem] rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
          {views.filter(([key]) => !lockedClosed || closedViews.has(key)).map(([key, label, hint]) => (
            <MenuItem key={key}>
              <button
                type="button"
                className={`flex w-full items-start justify-between gap-4 rounded-md px-2 py-1.5 text-left text-sm data-[focus]:bg-gray-100 ${view === key ? 'font-semibold' : ''}`}
                onClick={() => { setView(key); setPage(1); }}
              >
                <span className="min-w-0">
                  <span className="block whitespace-nowrap">{label}</span>
                  <span className="block whitespace-nowrap text-xs font-normal text-gray-500">{hint}</span>
                </span>
                {view === key && <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" />}
              </button>
            </MenuItem>
          ))}
        </MenuItems>
      </Menu>
      {!lockedClosed && (
        <Menu as="div" className="relative">
          <MenuButton type="button" className={`inline-flex h-[38px] items-center gap-2 whitespace-nowrap rounded-md border bg-white px-3 text-sm ${status !== defaultStatus ? 'border-gray-900' : 'border-gray-300'}`}>
            <span className="text-gray-500">Status:</span>
            <span className="font-semibold">{selectedStatus}</span>
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          </MenuButton>
          <MenuItems anchor="bottom end" className="z-30 mt-2 min-w-[15rem] rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
            {statuses.map(([key, label]) => (
              <MenuItem key={key}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-4 rounded-md px-2 py-2 text-left text-sm data-[focus]:bg-gray-100 ${status === key ? 'font-semibold' : ''}`}
                  onClick={() => { setStatus(key); setPage(1); }}
                >
                  <span>{label}</span>
                  {status === key && <CheckIcon className="h-4 w-4 shrink-0" />}
                </button>
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>
      )}
      <input className="h-[38px] w-36 rounded-md border border-gray-300 bg-white px-3 text-sm" value={vendor} onChange={(event) => setVendor(event.target.value)} placeholder="Vendor ID" />
      <input className="h-[38px] w-48 rounded-md border border-gray-300 bg-white px-3 text-sm" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search PO" />
      {hasFilters && (
        <button type="button" className="h-[38px] whitespace-nowrap text-sm text-blue-600" onClick={() => { setView(defaultView); setStatus(defaultStatus); setVendor(''); setSearch(''); setPage(1); }}>
          Clear filters
        </button>
      )}
    </div>
  );

  return (
    <ReportTemplate
      reportId={reportId}
      title={lockedClosed ? 'Closed PO Reconciliation' : 'PO Status Audit'}
      description="Read-only PO status recommendations and reconciliation evidence."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename={reportId} onExport={exportData} />}
      pagination={<div className="flex justify-between text-sm"><span>{data.length} rows</span><div className="flex gap-2"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button><span>Page {page} of {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button></div></div>}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {cards.map((card) => <div key={card.key}><div className="text-xs text-gray-500">{card.key.replace(':', ' / ')}</div><strong>{card.count}</strong></div>)}
          <div><div className="text-xs text-gray-500">Total POs</div><strong>{summary.totals?.count || 0}</strong></div>
        </div>
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {materiality.map(([key, value]) => <div key={key}><div className="text-xs text-gray-500">{key}</div><strong>{value.count} / {currency(value.absolute_goods_owed)}</strong></div>)}
        </div>
        {lockedClosed && (
          <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-5">
            {[
              ['Reconciles', 'reconciles'],
              ['Residual owed', 'residual_owed'],
              ['Overpaid', 'overpaid'],
              ['Bill not linked', 'bill_not_linked'],
              ['Bill disagrees', 'bill_disagrees'],
            ].map(([label, key]) => (
              <div key={key}><div className="text-xs text-gray-500">{label}</div><strong>{summary.buckets?.[key] || 0}</strong></div>
            ))}
          </div>
        )}
        {lockedClosed && (
          <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
            {closedVerdicts.map(([verdict, count]) => (
              <div key={verdict}><div className="text-xs text-gray-500">{verdict}</div><strong>{count}</strong></div>
            ))}
          </div>
        )}
        <div className="overflow-auto rounded border bg-white"><table className="min-w-max text-left text-xs"><thead className="sticky top-0 bg-gray-100"><tr>{visibleColumns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead><tbody>{data.map((row) => <tr key={row.po_id}>{visibleColumns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2">{renderCell(row, column)}</td>)}</tr>)}</tbody></table></div>
      </div>
    </ReportTemplate>
  );
};

export default PoStatusAuditReport;
